import { useEffect, useLayoutEffect, useRef } from 'react';
import { useHistory } from 'react-router-dom';

export interface UseUnsavedChangesGuardOptions {
  /**
   * Whether the form currently holds unsaved changes. When `false`, navigation
   * is allowed without prompting.
   */
  isDirty: boolean;
  /**
   * Whether a save is in flight. While saving, navigation is allowed silently
   * (the post-save redirect must not be blocked).
   */
  isSaving: boolean;
  /**
   * Shows the owner's discard-confirmation modal. The owner decides the copy
   * and variant (e.g. edit vs create); the hook calls `onConfirm` when the
   * user chooses to discard and leave.
   */
  showDiscardModal: (onConfirm: () => void) => void;
}

/**
 * Blocks in-app route changes and browser unload while a form has unsaved
 * changes, prompting the owner's discard modal first. Shared by the
 * Applications/Work Orders and Invoices create/edit steppers, which previously
 * duplicated this `history.block` + `beforeunload` plumbing verbatim.
 *
 * Returns `allowNavigation()`, which the owner must call immediately before a
 * programmatic navigation it intends to permit (e.g. after a successful save or
 * a confirmed cancel) so the guard lets that navigation through.
 */
export default function useUnsavedChangesGuard({
  isDirty,
  isSaving,
  showDiscardModal
}: UseUnsavedChangesGuardOptions): { allowNavigation: () => void } {
  const history = useHistory();
  const allowNavigationRef = useRef(false);
  const hasMountedRef = useRef(false);

  // Always invoke the latest modal callback without re-subscribing the blocker.
  const showDiscardModalRef = useRef(showDiscardModal);
  showDiscardModalRef.current = showDiscardModal;

  // Defer blocking until after mount so we don't intercept the navigation that
  // brought us here.
  useEffect(() => {
    requestAnimationFrame(() => {
      hasMountedRef.current = true;
    });
  }, []);

  // Block in-app route navigation when the form is dirty. A layout effect so
  // that, when this screen unmounts, the blocker is released in the same commit
  // phase where the next screen's <Prompt> registers its own; a passive cleanup
  // ran after that mount and history warned about two prompts at once.
  useLayoutEffect(() => {
    const unblock = history.block(location => {
      if (!hasMountedRef.current || allowNavigationRef.current || isSaving) {
        return undefined;
      }
      if (!isDirty) {
        return undefined;
      }
      const navigateAway = () => {
        allowNavigationRef.current = true;
        history.push(location.pathname + location.search);
      };
      showDiscardModalRef.current(navigateAway);
      return false;
    });

    return () => {
      unblock();
    };
  }, [history, isDirty, isSaving]);

  // Warn on browser tab close/refresh.
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isSaving || !isDirty) return;
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty, isSaving]);

  const allowNavigation = () => {
    allowNavigationRef.current = true;
  };

  return { allowNavigation };
}
